import {useEffect,useRef,useState} from "react";
import {api} from "@/lib/api";
import {selectAssistantVoice,speechParts,JARVIS_VOICE} from "@/lib/voiceStyle";
const SR=typeof window!=="undefined"?(window.SpeechRecognition||window.webkitSpeechRecognition):null;
export function useVoice(settings,onFinal){
 const [state,setState]=useState("inactivo"),[interim,setInterim]=useState(""),[error,setError]=useState("");
 const [continuous,setContinuous]=useState(false),[paused,setPaused]=useState(false),[micActive,setMicActive]=useState(false),[wakeActive,setWakeActive]=useState(false);
 const [wakeArmed,setWakeArmed]=useState(false);
 const [voiceName,setVoiceName]=useState(""),[assistantMode,setAssistantMode]=useState("normal"),[visibilityTick,setVisibilityTick]=useState(0);
 const config=useRef(settings),final=useRef(onFinal),mode=useRef(assistantMode),actions=useRef({});
 config.current=settings;final.current=onFinal;mode.current=assistantMode;
 const c=useRef({alive:true,enabled:false,paused:false,phase:"inactivo",seq:0,rec:null,response:null,errors:0,timers:{},wake:null,utterance:null});
 const supported={stt:!!SR,tts:typeof window!=="undefined"&&"speechSynthesis" in window};
 const phase=next=>{c.current.phase=next;if(c.current.alive)setState(next)};
 const clear=name=>{clearTimeout(c.current.timers[name]);delete c.current.timers[name]};
 const later=(name,fn,ms)=>{clear(name);c.current.timers[name]=setTimeout(()=>{delete c.current.timers[name];if(c.current.alive)fn()},ms)};
 const abortRecognition=()=>{
  const x=c.current;x.seq++;for(const n of ["turn","rec-start","rec-idle","resume"])clear(n);
  const rec=x.rec;x.rec=null;if(c.current.alive)setMicActive(false);if(rec)try{rec.abort()}catch{}
 };
 const cancelSpeech=()=>{clear("speech-start");clear("speech-end");c.current.utterance=null;if(supported.tts)window.speechSynthesis.cancel()};
 const abortWake=()=>{const w=c.current.wake;c.current.wake=null;if(w)try{w.abort()}catch{};if(c.current.alive)setWakeActive(false)};
 const stopSession=(interrupted=false)=>{
  const x=c.current;x.enabled=false;x.paused=false;x.response=null;x.errors=0;setWakeArmed(false);
  abortRecognition();abortWake();cancelSpeech();setContinuous(false);setPaused(false);setInterim("");phase(interrupted?"interrumpido":"inactivo");
  if(interrupted)later("interrupted",()=>phase("inactivo"),900);
 };
 const pauseSession=message=>{
  setWakeArmed(false);c.current.paused=true;c.current.response=null;abortRecognition();abortWake();cancelSpeech();setPaused(true);setInterim("");phase("inactivo");if(message)setError(message);
 };
 const resumeLater=(ms=450)=>{
  if(c.current.enabled&&!c.current.paused&&!document.hidden)later("resume",()=>actions.current.startListening(),ms);
 };
 const complete=(id)=>{
  if(id&&c.current.response!==id)return;
  c.current.response=null;clear("speech-start");clear("speech-end");c.current.utterance=null;phase("inactivo");resumeLater();
 };
 const startListening=()=>{
  if(!SR){setError("Este navegador no ofrece reconocimiento de voz. Podés escribir.");phase("error");return;}
  if(document.hidden||c.current.paused)return;
  abortRecognition();abortWake();cancelSpeech();c.current.response=null;
  const my=c.current.seq,segments=new Map();let rec;
  try{rec=new SR()}catch{pauseSession("El navegador no pudo preparar el micrófono. Podés escribir o volver a intentarlo.");return;}
  let submitted=false,failed=false;
  const dispatch=()=>{
   if(submitted||my!==c.current.seq||!c.current.alive)return;
   const text=[...segments.entries()].sort((a,b)=>a[0]-b[0]).map(([,v])=>v).join(" ").replace(/\s+/g," ").trim();
   if(!text)return;
   submitted=true;abortRecognition();c.current.errors=0;setInterim("");phase("pensando");final.current?.(text);
  };
  rec.lang=config.current?.lang||"es-UY";rec.interimResults=true;rec.continuous=false;rec.maxAlternatives=1;
  rec.onstart=()=>{if(my!==c.current.seq)return;clear("rec-start");setMicActive(true);phase("escuchando")};
  rec.onresult=e=>{
   if(my!==c.current.seq||submitted)return;
   clear("rec-idle");later("rec-idle",()=>{abortRecognition();phase("inactivo");resumeLater(650)},30000);
   let temporary="";for(let i=0;i<e.results.length;i++){const r=e.results[i];if(r.isFinal)segments.set(i,r[0].transcript);else temporary+=r[0].transcript+" ";}
   const text=[...segments.values()].join(" ");setInterim((text+" "+temporary).trim());
   clear("turn");
   if(temporary.trim())later("turn",()=>{if(my===c.current.seq&&!submitted)try{rec.stop()}catch{}},1800);
   else if(segments.size)later("turn",dispatch,Math.max(400,Math.min(2500,config.current?.end_turn_ms??750)));
  };
  rec.onerror=e=>{
   if(my!==c.current.seq||submitted)return;failed=true;
   if(e.error==="no-speech"||e.error==="aborted"){abortRecognition();phase("inactivo");resumeLater(700);return;}
   const fatal=["not-allowed","service-not-allowed","audio-capture"].includes(e.error);
   const msg=fatal?"El navegador no pudo abrir el micrófono. Revisá su permiso y tocá Reanudar.":"Se interrumpió el reconocimiento de voz. Podés reanudarlo.";
   c.current.errors++;
   if(fatal||c.current.errors>=3){pauseSession(msg);return;}
   abortRecognition();setError(msg);phase("inactivo");resumeLater(700*c.current.errors);
  };
  rec.onend=()=>{
   if(my!==c.current.seq||submitted||failed)return;
   clear("rec-start");clear("rec-idle");setMicActive(false);
   if(segments.size){dispatch();return;}abortRecognition();phase("inactivo");resumeLater(650);
  };
  c.current.rec=rec;setError("");setInterim("");phase("escuchando");
  later("rec-start",()=>pauseSession("El micrófono no se inició. Tocá Reanudar para volver a intentarlo."),8000);
  later("rec-idle",()=>{abortRecognition();phase("inactivo");resumeLater(650)},30000);
  try{rec.start()}catch{pauseSession("El navegador no inició la escucha. Tocá Reanudar para continuar.")}
 };
 actions.current.startListening=startListening;
 const unlockSpeech=()=>{
  if(!supported.tts)return;
  const synth=window.speechSynthesis;synth.cancel();synth.resume?.();
  // A silent utterance in the explicit tap unlocks synthesis on gesture-gated browsers.
  try{const u=new SpeechSynthesisUtterance(" ");u.volume=0;synth.speak(u);synth.cancel()}catch{}
 };
 const startConversation=()=>{
  if(!SR){setError("Voz no disponible en este navegador. Podés escribir.");phase("error");return;}
  clear("interrupted");setWakeArmed(false);unlockSpeech();c.current.enabled=true;c.current.paused=false;c.current.errors=0;
  setError("");setInterim("");setContinuous(true);setPaused(false);startListening();
 };
 const toggleConversation=()=>{if(c.current.enabled&&!c.current.paused)stopSession();else startConversation()};
 const beginResponse=(id=>{
  clear("interrupted");abortRecognition();abortWake();cancelSpeech();c.current.response=id;setInterim("");phase("pensando");
 });
 const speak=((text,id)=>{
  if(c.current.response!==id)return;
  const parts=speechParts(text);
  if(!supported.tts||!parts.length){complete(id);return;}
  abortRecognition();abortWake();cancelSpeech();
  const synth=window.speechSynthesis,cfg=config.current||{},voice=selectAssistantVoice(synth.getVoices(),cfg);
  setVoiceName(voice?.name||"Voz del dispositivo");
  const session=c.current.seq;
  let at=0;
  const valid=()=>c.current.alive&&c.current.response===id&&session===c.current.seq;
  const next=()=>{
   if(!valid())return;
   if(at>=parts.length){complete(id);return;}
   const u=new SpeechSynthesisUtterance(parts[at++]);c.current.utterance=u;
   u.lang=voice?.lang||cfg.lang||"es-UY";u.rate=Math.max(.6,Math.min(1.6,(cfg.rate??JARVIS_VOICE.rate)*(mode.current==="gaming"?1.08:mode.current==="relajado"?.94:1)));u.pitch=Math.max(.5,Math.min(1.5,cfg.pitch??JARVIS_VOICE.pitch));u.volume=Math.max(.05,Math.min(1,cfg.tts_volume??1));
   if(voice)u.voice=voice;
   u.onstart=()=>{if(!valid())return;clear("speech-start");phase("hablando")};
   u.onend=()=>{if(!valid()||c.current.utterance!==u)return;clear("speech-start");clear("speech-end");next()};
   u.onerror=()=>{if(valid()&&c.current.utterance===u)pauseSession("El navegador interrumpió la voz. La respuesta queda escrita; podés tocar Reanudar.")};
   later("speech-start",()=>{if(valid())pauseSession("El navegador no inició la voz. Tocá Reanudar para continuar.")},6000);
   later("speech-end",()=>{if(valid())pauseSession("La voz del navegador quedó en pausa. La respuesta sigue disponible por escrito.")},Math.min(45000,6000+u.text.length*160));
   try{synth.speak(u)}catch{pauseSession("El navegador no pudo reproducir la voz. La respuesta queda escrita; podés reanudar.");}
  };
  synth.resume?.();next();
 });
 const finishSilently=(id=>complete(id));
 const fail=((msg,id)=>{if(id&&c.current.response!==id)return;c.current.response=null;abortRecognition();cancelSpeech();setError(msg);phase("error");resumeLater(900)});
 const interrupt=(()=>stopSession(true));
 const listen=(()=>{c.current.paused=false;setPaused(false);unlockSpeech();startListening()});
 const stopListening=(()=>stopSession());
 const stopConversation=(()=>stopSession());
 const pauseConversation=(()=>pauseSession());
 const interruptAndListen=(()=>{
  clear("interrupted");abortRecognition();abortWake();cancelSpeech();c.current.response=null;
  c.current.enabled=true;c.current.paused=false;c.current.errors=0;setWakeArmed(false);
  setContinuous(true);setPaused(false);setError("");setInterim("");unlockSpeech();startListening();
 });
 const toggleWake=(()=>{
  if(wakeArmed){setWakeArmed(false);abortWake();return;}
  stopSession();setError("");setWakeArmed(true);unlockSpeech();
 });
 const resumeConversation=(()=>startConversation());
 useEffect(()=>{
  let active=true;api.get("/assistant/preferences").then(r=>{if(active)setAssistantMode(r.data.mode)}).catch(()=>{});
  return()=>{active=false};
 },[]);
 actions.current.pauseSession=pauseSession;
 actions.current.deactivate=()=>{setWakeArmed(false);abortRecognition();abortWake();cancelSpeech();c.current.response=null;phase("inactivo")};
 actions.current.dispose=()=>{const x=c.current;x.alive=false;x.enabled=false;x.response=null;for(const n of Object.keys(x.timers))clear(n);abortRecognition();abortWake();cancelSpeech()};
 useEffect(()=>{
  c.current.alive=true;
  const lifecycle=actions.current;
  const visibility=event=>{
   if(document.hidden||event?.type==="pagehide"){if(c.current.enabled)actions.current.pauseSession("Conversación pausada al salir de JARVIS. Tocá Reanudar al volver.");else actions.current.deactivate()}
   setVisibilityTick(v=>v+1);
  };
  document.addEventListener("visibilitychange",visibility);window.addEventListener("pagehide",visibility);
  return()=>{lifecycle.dispose();document.removeEventListener("visibilitychange",visibility);window.removeEventListener("pagehide",visibility)};
 },[]);
 // The previously configured wake word remains separate; a conversation session has priority.
 useEffect(()=>{
  if(!wakeArmed||!settings?.wake_word||!SR||continuous||paused||state!=="inactivo"||document.hidden)return;
  const controller=c.current;
  let stopped=false,timer;
  const start=()=>{
   if(stopped||c.current.enabled||document.hidden||c.current.phase!=="inactivo")return;
   const w=new SR();c.current.wake=w;w.lang=settings?.lang||"es-UY";w.continuous=true;w.interimResults=false;
   w.onresult=e=>{if(stopped||c.current.enabled)return;const text=e.results[e.results.length-1][0].transcript,at=text.toLocaleLowerCase().indexOf("jarvis");if(at<0)return;stopped=true;try{w.abort()}catch{}setWakeActive(false);const rest=text.slice(at+6).trim();if(rest.length>2){controller.phase="pensando";setState("pensando");final.current?.(rest)}else actions.current.startListening()};
   w.onerror=e=>{if(["not-allowed","service-not-allowed","audio-capture"].includes(e.error)){stopped=true;actions.current.pauseSession("No se pudo activar la palabra Jarvis. Revisá el permiso del micrófono y tocá Reanudar.");}};
   w.onend=()=>{setWakeActive(false);if(!stopped)timer=setTimeout(start,900)};
   try{w.start();setWakeActive(true)}catch{}
  };
  start();return()=>{stopped=true;clearTimeout(timer);const w=controller.wake;controller.wake=null;if(w)try{w.abort()}catch{}if(controller.alive)setWakeActive(false)};
 },[wakeArmed,settings?.wake_word,settings?.lang,state,continuous,paused,visibilityTick]);
 return {state,interim,error,supported,wakeActive,wakeArmed,toggleWake,pauseConversation,interruptAndListen,continuous,paused,micActive,voiceName,listen,stopListening,interrupt,beginResponse,speak,finishSilently,fail,toggleConversation,startConversation,resumeConversation,stopConversation};
}
